import React, { useState, useRef, useEffect, useMemo } from 'react';
import './SearchRoom.css';
import { useTranslation } from 'react-i18next';
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/material_orange.css";

// Εισαγωγή της ελληνικής γλώσσας
import { Greek } from "flatpickr/dist/l10n/gr.js";

const SearchRoom = () => {
  const { t, i18n } = useTranslation();
  
  // ΔΙΟΡΘΩΣΗ 1: Αρχικοποίηση με άδειο πίνακα αντί για [null, null]
  const [dates, setDates] = useState([]);

  // --- HELPER: Μορφοποίηση Ημερομηνίας ---
  const formatDate = (date) => {
    if (!date || !(date instanceof Date)) return "-";
    const locale = (i18n.language === 'gr' || i18n.language === 'el') ? 'el-GR' : 'en-GB';
    return date.toLocaleDateString(locale, { day: 'numeric', month: 'short', year: 'numeric' });
  };

  // --- HELPER: Ενημέρωση του Footer ---
  const updateFooterDOM = (selectedDates, instance) => {
    const calendar = instance.calendarContainer;
    if (!calendar) return;

    const startSpan = calendar.querySelector("#footer-start");
    const endSpan = calendar.querySelector("#footer-end");
    const nightsSpan = calendar.querySelector("#footer-nights");

    if (!startSpan || !endSpan || !nightsSpan) return;

    if (selectedDates.length > 0 && selectedDates[0]) {
        startSpan.innerText = formatDate(selectedDates[0]);
    } else {
        startSpan.innerText = "-";
    }

    if (selectedDates.length > 1 && selectedDates[1]) {
        endSpan.innerText = formatDate(selectedDates[1]);
        const diffTime = Math.abs(selectedDates[1] - selectedDates[0]);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        
        const nightLabel = diffDays === 1 ? (t('widget.night') || 'νύχτα') : (t('widget.nights') || 'νύχτες');
        nightsSpan.innerText = `${diffDays} ${nightLabel}`;
    } else {
        endSpan.innerText = "-";
        nightsSpan.innerText = `0 ${t('widget.nights') || 'νύχτες'}`;
    }
  };

const [showMonths, setShowMonths] = useState(() => window.innerWidth < 500 ? 1 : 2);
const flatpickrRef = useRef(null); // ref to the flatpickr instance

useEffect(() => {
  let resizeTimer;
  const handleResize = () => {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(() => {
      const months = window.innerWidth < 500 ? 1 : 2;
      setShowMonths(prev => {
        if (prev !== months && flatpickrRef.current) {
          flatpickrRef.current.set("showMonths", months);
          flatpickrRef.current.redraw();
        }
        return months;
      });
    }, 150);
  };

  window.addEventListener("resize", handleResize);
  return () => {
    clearTimeout(resizeTimer);
    window.removeEventListener("resize", handleResize);
  };
}, []);

const flatpickrOptions = useMemo(() => {
  const currentLocale = (i18n.language === 'gr' || i18n.language === 'el') ? Greek : "default";

  return {
    mode: "range",
    minDate: "today",
    dateFormat: "Y-m-d",
    locale: currentLocale,
    showMonths, // now reactive
    altInput: true,
    altFormat: "d M Y",
    disable: [
      function(date) {
        const month = date.getMonth();
        const day = date.getDate();
        if (month >= 0 && month <= 4) return true;
        if (month >= 9 && month <= 11) return true;
        if (month === 5 && day <= 30) return true;
        if (month === 8 && day >= 6) return true;
        return false;
      }
    ],
    onReady: (selectedDates, dateStr, instance) => {
      // Store ref for resize handler
      flatpickrRef.current = instance;

      if (instance.calendarContainer.querySelector(".custom-flatpickr-footer")) return;

      const footer = document.createElement("div");
      footer.className = "custom-flatpickr-footer";

      footer.innerHTML = `
        <div class="footer-info">
          <span id="footer-nights" class="footer-bold" style="margin-right: 10px">
            0 ${t('widget.nights') || 'νύχτες'}
          </span>
          <span>
            <span class="footer-label">${t('widget.from') || 'Από'}</span>
            <span id="footer-start" class="footer-bold">-</span>
          </span>
          <span>
            <span class="footer-label">${t('widget.to') || 'Έως'}</span>
            <span id="footer-end" class="footer-bold">-</span>
          </span>
        </div>
        <button id="footer-clear" class="footer-clear-btn">
          ${t('widget.clear_dates') || 'Καθαρισμός'}
        </button>
      `;

      instance.calendarContainer.appendChild(footer);

      const clearBtn = footer.querySelector("#footer-clear");
      if (clearBtn) {
        clearBtn.addEventListener("click", () => {
          instance.clear();
          setDates([]);
        });
      }
      updateFooterDOM(selectedDates, instance);
    },
    onChange: (selectedDates, dateStr, instance) => {
      setDates(selectedDates);
      updateFooterDOM(selectedDates, instance);
    }
  };
}, [t, i18n.language, showMonths]); // showMonths added as dependency

  const nightCount = useMemo(() => {
    if (dates.length === 2 && dates[0] && dates[1]) {
      const diffTime = Math.abs(dates[1] - dates[0]);
      return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    }
    return 0;
  }, [dates]);

  const [showGuestPopup, setShowGuestPopup] = useState(false);
  const [rooms, setRooms] = useState([{ id: 1, adults: 2, children: 0 }]);
  const guestDropdownRef = useRef(null);
  
  useEffect(() => {
    function handleClickOutside(event) {
      if (guestDropdownRef.current && !guestDropdownRef.current.contains(event.target)) {
        setShowGuestPopup(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const addRoom = () => setRooms([...rooms, { id: rooms.length + 1, adults: 1, children: 0 }]);
  const removeRoom = (index) => setRooms(rooms.filter((_, i) => i !== index));
  const handleGuestChange = (index, type, op) => {
    const newRooms = [...rooms];
    if (type === 'adults') op === 'increment' ? newRooms[index].adults++ : newRooms[index].adults > 1 && newRooms[index].adults--;
    if (type === 'children') op === 'increment' ? newRooms[index].children++ : newRooms[index].children > 0 && newRooms[index].children--;
    setRooms(newRooms);
  };
  
  const getGuestText = () => {
     const totalRooms = rooms.length;
     const totalAdults = rooms.reduce((sum, r) => sum + r.adults, 0);
     const totalChildren = rooms.reduce((sum, r) => sum + r.children, 0);
     let textPart = `${totalRooms} ${totalRooms > 1 ? (t('widget.rooms') || 'δωμάτια') : (t('widget.room') || 'δωμάτιο')}, ${totalAdults} ${t('widget.adults_short') || 'εν.'}`;
     if (totalChildren > 0) { textPart += `, ${totalChildren} ${t('widget.children_short') || 'παιδ.'}`; }
     return <span>{textPart}</span>;
  };

  const handleSearch = () => {
    if (dates.length < 2) { 
        alert(t('widget.alertdate') || "Παρακαλώ επιλέξτε ημερομηνίες"); 
        return; 
    }

    const targetUrl = "https://rafkonapartments.reserve-online.net/";
    
    const toISODate = (d) => {
        if (!d) return '';
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const totalAdults = rooms.reduce((sum, room) => sum + room.adults, 0);
    const totalChildren = rooms.reduce((sum, room) => sum + room.children, 0);
    
    const queryParams = new URLSearchParams({
      checkin: toISODate(dates[0]), 
      rooms: rooms.length, 
      nights: nightCount, 
      adults: totalAdults, 
      children: totalChildren, 
      infants: 0
    }).toString();
    
    window.open(`${targetUrl}?${queryParams}`, '_blank');
  };

  return (
      <div className="search-bar-container">
        <div className="search-field location disabled-field">
          <svg className="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg>
          <input type="text" value={t('widget.choose') || "Επιλέξτε Τοποθεσία"} disabled className="input-text bold-text" />
        </div>
        <div className="divider"></div>
        <div className="search-field dates">
          <svg className="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect><line x1="16" y1="2" x2="16" y2="6"></line><line x1="8" y1="2" x2="8" y2="6"></line><line x1="3" y1="10" x2="21" y2="10"></line></svg>
          <div className="date-inputs flatpickr-wrapper">
            <Flatpickr 
              placeholder={t('widget.datesSelected')} 
              options={flatpickrOptions} 
              value={dates} 
              onChange={(selectedDates) => setDates(selectedDates)} 
            />
          </div>
        </div>
        <div className="divider"></div>
        <div className="search-field guests" ref={guestDropdownRef}>
            <div className="guest-trigger" onClick={() => setShowGuestPopup(!showGuestPopup)}>
            {nightCount > 0 && (
                <span className="nights-badge">
                    {nightCount} {nightCount > 1 ? (t('widget.nights') || 'νύχτες') : (t('widget.night') || 'νύχτα')}
                </span>
            )}  
            <svg className="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle></svg>
            <span className="text-content">{getGuestText()}</span>
          </div>
          {showGuestPopup && (
            <div className="guest-popup">
              <div className="rooms-scroll-container">
                {rooms.map((room, index) => (
                  <div key={index} className="room-row">
                    <div className="room-header-group">
                      {index > 0 && (<button type="button" className="counter-btn" onClick={() => removeRoom(index)}>-</button>)}
                      <span className="room-label">{t('widget.room')} {index + 1}</span>
                    </div>
                    <div className="counter-group">
                       <span className="counter-label">{t('widget.adults')} (18+)</span>
                       <div className="counter-controls">
                         <button type="button" className="counter-btn" onClick={() => handleGuestChange(index, 'adults', 'decrement')} disabled={room.adults <= 1}>-</button>
                         <span className="counter-value">{room.adults}</span>
                         <button type="button" className="counter-btn" onClick={() => handleGuestChange(index, 'adults', 'increment')}>+</button>
                       </div>
                    </div>
                    <div className="counter-group">
                       <span className="counter-label">{t('widget.children')}</span>
                       <div className="counter-controls">
                         <button type="button" className="counter-btn" onClick={() => handleGuestChange(index, 'children', 'decrement')} disabled={room.children <= 0}>-</button>
                         <span className="counter-value">{room.children}</span>
                         <button type="button" className="counter-btn" onClick={() => handleGuestChange(index, 'children', 'increment')}>+</button>
                       </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="popup-footer">
                <span className="add-room" onClick={addRoom}>+ {t('widget.addroom') || "Προσθήκη Δωματίου"}</span>
                <button type="button" className="validate-btn" onClick={() => setShowGuestPopup(false)}>{t('widget.validate') || "Έτοιμο"}</button>
              </div>
            </div>
          )}
        </div>
        <div className="divider"></div>
        <button className="search-btn" onClick={handleSearch}>{t('widget.search') || "Αναζήτηση"}</button>
      </div>

  );
}

export default SearchRoom;